/**
 * Calculates the minimum and maximum values from chart data
 * @param {Array} chartData Array of data objects for the chart
 * @param {Array} raktai Keys to consider when finding min/max values
 * @returns {Object [Number, Number]} Object containing min and max values
 */
export function grafikoReziai(chartData, raktai) {
  let min = Number.MAX_VALUE;
  let max = Number.MIN_VALUE;

  chartData.forEach((dataPoint) => {
    raktai.forEach((key) => {
      const value = dataPoint[key];
      // Only consider numeric values that aren't null
      if (value !== null && !isNaN(value)) {
        min = Math.min(min, value);
        max = Math.max(max, value);
      }
    });
  });

  if (min === Number.MAX_VALUE) min = 0;
  if (max === Number.MIN_VALUE) max = 100;

  return { min, max };
}

/**
 * Function used for extracting specific data from array of object arrays.
 * @param {Array [Object[...props, Object[Object[...props]]]]} data Array of data where object has multiple sub object array.
 * @param {String} keyObject Sub object array to be used for calculation.
 * @param {String} columnToIgnore Specifies, which object from @param keyObject should not be
 *                                used for key generation
 * @returns {Object [yAxisMin: Number, yAxisMax: Number, chartData: Object[...props],
 *          raktai: Object[...props]]} yAxisMax: max y axis value for object array,
 *                                     yAxisMin: yAxisMax: max y axis value for object array,
 *                                     chartData: extracted specific @param keyObject data,
 *                                     raktai: extracted key that will be use x axis stacks.
 *                                     @param columnToIgnore is used for X axis plotting
 */
export function grafikoDuomenys(data, keyObject, columnToIgnore, excludeNull = false) {
  let chartData = keyObject !== null ? data[keyObject] : data;

  if (!chartData || chartData.length === 0) {
    return { yAxisMin: 0, yAxisMax: 100, chartData: [], raktai: [] };
  }

  let raktai = Object.keys(chartData[0]).filter((key) => key !== columnToIgnore);

  if (excludeNull) {
    raktai = raktai.filter((key) => {
      return chartData.some((item) => item[key] !== null && item[key] !== undefined);
    });

    chartData = chartData.filter((item) => {
      return raktai.some((key) => item[key] !== null && item[key] !== undefined);
    });

    if (chartData.length === 0 || raktai.length === 0) {
      return { yAxisMin: 0, yAxisMax: 100, chartData: [], raktai: [] };
    }
  }

  const { min, max } = grafikoReziai(chartData, raktai);

  const yAxisMin = Math.floor(min * 0.97);
  const yAxisMax = Math.ceil(max * 1.03);

  return { yAxisMin, yAxisMax, chartData, raktai };
}
export function calculateAbsoluteMinMax(values) {
  let absoluteMin = Number.MAX_VALUE;
  let absoluteMax = Number.MIN_VALUE;
  values.forEach((value) => {
    absoluteMin = Math.min(absoluteMin, value.min);
    absoluteMax = Math.max(absoluteMax, value.max);
  });

  return { absoluteMin, absoluteMax };
}

/**
 * Checks if all non-ignored values in all objects of an array are null
 * @param {Array<Object>} data - The array of objects to check
 * @param {String} columnToIgnore - Key to ignore when checking for null values (e.g., 'Mėnesis')
 * @returns {Boolean} - True if all values (excluding ignored key) are null, otherwise false
 */
export function isAllDataNull(data, columnToIgnore = "Mėnesis") {
  if (!Array.isArray(data) || data.length === 0) return true;

  return data.every((item) => {
    return Object.entries(item).every(([key, value]) => {
      return key === columnToIgnore || value === null;
    });
  });
}

export function grafikoReziaiSuVienuraktu(data, key, step) {
  let min = Infinity;
  let max = -Infinity;

  data.forEach((item) => {
    if (item[key] !== null && !isNaN(item[key])) {
      min = Math.min(min, item[key]);
      max = Math.max(max, item[key]);
    }
  });

  // if no valid values found
  if (!isFinite(min) || !isFinite(max)) {
    return { min: 0, max: step };
  }

  const flooredMin = Math.floor(min / step) * step;
  const ceiledMax = Math.ceil(max / step) * step;

  return { min: flooredMin, max: ceiledMax };
}

export function grafikoReziaiIrMastelisSuVienuraktu(data, key, step, defaultMin = null) {
  let min = Infinity;
  let max = -Infinity;

  data.forEach((item) => {
    if (item[key] !== null && !isNaN(item[key])) {
      min = Math.min(min, item[key]);
      max = Math.max(max, item[key]);
    }
  });

  // if no valid values found
  if (!isFinite(min) || !isFinite(max)) {
    return { min: 0, max: step };
  }

  let flooredMin = Math.floor(min / step) * step;
  flooredMin = defaultMin != null ? Math.min(flooredMin, defaultMin) : flooredMin;
  const ceiledMax = Math.ceil(max / step) * step;
  let minTick = flooredMin;
  let maxTick = ceiledMax;
  let midTick = (minTick + maxTick) / 2;

  return { min: flooredMin, max: ceiledMax, minTick, maxTick, midTick };
}
